import type { Observable } from 'rxjs';
import type { SitePageEntity } from '@common/types/site-page.type';
import type { Domain } from '@common/types/domain.type';
import type { PromoCode } from '@integrations/promo-codes';

import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';
import { AsyncPipe } from '@angular/common';

import type { FormAdditionalSettingsData } from '@sites/entity-core';
import type { PaymentFormWidgetForm } from '@sites/entity-signal-form';

import { injectPromoCodesQuery } from '@integrations/promo-codes';

import { PagesFacade } from '@web/store/website/pages/pages.facade';
import { DomainsFacade } from '@web/store/website/domains/domains.facade';
import { LayoutFacade } from '@web/store/layout/layout.facade';

import { environment } from '@web-env/environment';
import { CommonElementsModule } from '@web/widgets/common/common-elements.module';
import { SpFormAdditionalSettingsSignalComponent } from '@components/sp-form-additional-settings-signal';
import { FormSettingsPanel } from '@web/widgets/common/abstract/form-settings-panel';

/**
 * Breadcrumbs/footer chrome around `sp-form-additional-settings-signal`,
 * writing straight into the payment-form signal form. Save/cancel and the
 * transaction frame come from `FormSettingsPanel`.
 */
@Component({
    selector: 'web-payment-additional-settings',
    templateUrl: './payment-additional-settings.component.html',
    styleUrls: ['./payment-additional-settings.component.less'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [CommonElementsModule, SpFormAdditionalSettingsSignalComponent, AsyncPipe],
})
export class PaymentAdditionalSettingsComponent extends FormSettingsPanel<PaymentFormWidgetForm> {
    public readonly isHasSuccessPageOption = input<boolean>(false);

    private readonly pagesFacade: PagesFacade = inject(PagesFacade);
    private readonly domainsFacade: DomainsFacade = inject(DomainsFacade);
    private readonly layoutFacade: LayoutFacade = inject(LayoutFacade);
    private readonly promoCodesQuery = injectPromoCodesQuery();

    public readonly pages$: Observable<SitePageEntity[]> = this.pagesFacade.pages$;
    public readonly domains$: Observable<Domain[]> = this.domainsFacade.domains$;
    public readonly promoCodes = computed<PromoCode[]>(() => this.promoCodesQuery.data() ?? []);

    public readonly baseHref = environment.baseHref;
    public readonly selectedDeviceMode$ = this.layoutFacade.deviceMode$;

    public readonly settings = computed<FormAdditionalSettingsData>(() => this.form().additionalFormSettings().value());

    public onSettingsChange(value: FormAdditionalSettingsData): void {
        this.form().setAdditionalFormSettings(value);
    }
}
